import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

// 구글 Apps Script 웹 앱 주소. 배포 환경변수 VITE_FORM_URL 로 지정하거나 아래 기본값이 현재 배포된 주소입니다.
const FORM_URL = import.meta.env.VITE_FORM_URL || 'https://script.google.com/macros/s/AKfycbysOV5S8RORI_jqvo2zhjP-cptV-zQn0Tj2AeYkiw2zJHsOXepKobGl7Qkbc7A-WWtDRA/exec';

export default function ContactSection() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [textLen, setTextLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setLoading(true);
    setFailed(false);
    const params = new URLSearchParams();
    new FormData(form).forEach((val, key) => params.append(key, val.toString()));

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: params.toString(),
      });
      const result = await res.json();
      if (result.result !== 'success') throw new Error(result.error || 'send failed');
      setSubmitted(true);
      form.reset();
      setTextLen(0);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: 'ri-smartphone-fill', labelKey: 'contact_info_mobile', value: '010-7375-1750', href: 'tel:010-7375-1750' },
    { icon: 'ri-phone-fill', labelKey: 'contact_info_phone', value: '02-6941-1750', href: 'tel:02-6941-1750' },
    { icon: 'ri-printer-fill', labelKey: 'contact_info_fax', value: '02-6919-1781', href: undefined },
    { icon: 'ri-mail-fill', labelKey: 'contact_info_email', value: 'workmin@sngsteel.com', href: 'mailto:workmin@sngsteel.com' },
    { icon: 'ri-map-pin-fill', labelKey: 'contact_info_address', value: '서울특별시 용산구 한강대로 69 업무동 702호', href: undefined },
  ];

  return (
    <section id="contact" className="w-full bg-[#F8F7F4] py-16 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Left Info */}
          <div>
            <div className="text-[#E8762B] text-xs font-semibold tracking-widest mb-4">{t('contact_section_label')}</div>
            <h2 className="text-3xl md:text-4xl font-black text-[#1C2E4A] leading-tight mb-6" style={{ fontFamily: "'Noto Serif KR', serif" }}>
              {t('contact_title_line1')}<br />
              {t('contact_title_line2')}
            </h2>
            <p className="text-[#4A5568] leading-relaxed mb-8 md:mb-10 text-sm md:text-base">
              {t('contact_subtitle')}
            </p>

            {/* Contact Info */}
            <div className="space-y-4 md:space-y-5">
              {contactItems.map((c) => (
                <div key={c.labelKey} className="flex items-start gap-3 md:gap-4">
                  <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-[#1C2E4A] rounded-lg mt-0.5 shrink-0">
                    <i className={`${c.icon} text-[#E8762B] text-sm md:text-base`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-[#9CA3AF] mb-0.5">{t(c.labelKey)}</div>
                    {c.href ? (
                      <a href={c.href} className="text-[#1C2E4A] font-semibold text-sm hover:text-[#E8762B] transition-colors cursor-pointer">
                        {c.value}
                      </a>
                    ) : (
                      <div className="text-[#1C2E4A] font-semibold text-sm">{c.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="mt-8 md:mt-10 rounded-xl overflow-hidden h-44 md:h-48 border border-gray-100">
              <iframe
                title={t('contact_map_title')}
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3163.207!2d126.9642!3d37.5291!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357ca2e1a8b87985%3A0x0!2z7ISc7Jq47Yq567OE7IucIOyEseyLnOq1rCDtmLjqtawgNjk!5e0!3m2!1sko!2skr!4v1"
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-2xl p-6 md:p-10 border border-gray-100">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-[#E8762B]/10 rounded-full mb-5">
                  <i className="ri-checkbox-circle-fill text-3xl text-[#E8762B]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1C2E4A] mb-2">{t('form_success_title')}</h3>
                <p className="text-[#6B7280] text-sm">{t('form_success_desc')}</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm text-[#E8762B] underline cursor-pointer whitespace-nowrap"
                >
                  {t('form_success_again')}
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-4 md:space-y-5"
              >
                <h3 className="text-lg md:text-xl font-black text-[#1C2E4A] mb-4 md:mb-6">{t('contact_form_title')}</h3>

                {/* 스팸 봇 차단용 숨김 입력칸 */}
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                      {t('form_name_label')} <span className="text-[#E8762B]">{t('form_required')}</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder={t('form_name_placeholder')}
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-[#1C2E4A] focus:outline-none focus:border-[#E8762B] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#374151] mb-1.5">{t('form_company_label')}</label>
                    <input
                      type="text"
                      name="company"
                      placeholder={t('form_company_placeholder')}
                      className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-[#1C2E4A] focus:outline-none focus:border-[#E8762B] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                    {t('form_email_label')} <span className="text-[#E8762B]">{t('form_required')}</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="example@company.com"
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-[#1C2E4A] focus:outline-none focus:border-[#E8762B] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                    {t('form_phone_label')} <span className="text-[#E8762B]">{t('form_required')}</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder={t('form_phone_placeholder')}
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-[#1C2E4A] focus:outline-none focus:border-[#E8762B] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1.5">{t('form_product_label')}</label>
                  <select
                    name="product"
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-[#1C2E4A] focus:outline-none focus:border-[#E8762B] transition-colors bg-white cursor-pointer"
                  >
                    <option value="">{t('form_product_placeholder')}</option>
                    <option value="Pipe">{t('form_product_pipe')}</option>
                    <option value="Fitting">{t('form_product_fitting')}</option>
                    <option value="Flange">{t('form_product_flange')}</option>
                    <option value="Pile">{t('form_product_pile')}</option>
                    <option value="PE Coating">{t('form_product_pe')}</option>
                    <option value="Other">{t('form_product_other')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                    {t('form_message_label')} <span className="text-[#E8762B]">{t('form_required')}</span>
                    <span className="text-gray-400 ml-2 font-normal">{textLen}/500</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder={t('form_message_placeholder')}
                    onChange={(e) => setTextLen(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm text-[#1C2E4A] focus:outline-none focus:border-[#E8762B] transition-colors resize-none"
                  />
                  {textLen > 500 && (
                    <p className="text-red-500 text-xs mt-1">{t('form_message_limit')}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || textLen > 500}
                  className="w-full bg-[#1C2E4A] hover:bg-[#263D5E] text-white py-3.5 rounded-md font-bold text-sm transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      {t('form_sending')}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      {t('form_submit')}
                    </>
                  )}
                </button>

                {failed && (
                  <p className="text-red-500 text-xs text-center">{t('form_error')}</p>
                )}

                <p className="text-xs text-[#9CA3AF] text-center">
                  {t('form_notice')}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
